import { authClient } from "@/api/auth-client";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FormError } from "@/shared/components/form-error";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "@tanstack/react-router";
import { type RequestPasswordResetInput, requestPasswordResetSchema } from "@template/shared";
import { useState } from "react";
import { useForm } from "react-hook-form";

/** Zapomenuté heslo — uživatel zadá e-mail, přijde mu odkaz na reset. */
export function ForgotPasswordForm() {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RequestPasswordResetInput>({
    resolver: zodResolver(requestPasswordResetSchema),
  });

  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const onSubmit = handleSubmit(async (data) => {
    setIsLoading(true);
    setServerError(null);
    await authClient.requestPasswordReset({
      email: data.email,
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setIsLoading(false);
    // Neprozrazujeme, jestli e-mail existuje — vždy stejná zpráva (anti-enumerace).
    setSent(true);
  });

  if (sent) {
    return (
      <Card className="w-full max-w-md" data-testid="auth-forgot-card">
        <CardHeader className="text-center">
          <CardTitle>Zkontrolujte e-mail</CardTitle>
          <CardDescription>
            Pokud u nás účet s touto adresou existuje, poslali jsme na něj odkaz pro nastavení
            nového hesla. Platí 24 hodin.
          </CardDescription>
        </CardHeader>
        <CardFooter className="justify-center">
          <Link
            to="/login"
            className="text-sm font-medium text-foreground hover:underline"
            data-testid="auth-forgot-back-login"
          >
            Zpět na přihlášení
          </Link>
        </CardFooter>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md" data-testid="auth-forgot-card">
      <CardHeader className="text-center">
        <CardTitle>Zapomenuté heslo</CardTitle>
        <CardDescription>Zadejte e-mail a pošleme vám odkaz pro nastavení hesla.</CardDescription>
      </CardHeader>
      <CardContent>
        <FormError message={serverError ?? undefined} testId="auth-forgot-error" />
        <form onSubmit={onSubmit} className="space-y-4" data-testid="auth-forgot-form">
          <div className="space-y-2">
            <Label htmlFor="forgot-email">E-mail</Label>
            <Input
              id="forgot-email"
              type="email"
              autoFocus
              aria-describedby={errors.email ? "forgot-email-error" : undefined}
              data-testid="auth-forgot-email-input"
              {...register("email")}
            />
            {errors.email ? (
              <p id="forgot-email-error" role="alert" className="text-sm text-destructive">
                {errors.email.message}
              </p>
            ) : null}
          </div>
          <Button
            type="submit"
            className="w-full"
            disabled={isLoading}
            data-testid="auth-forgot-submit-button"
          >
            {isLoading ? "Odesílám…" : "Poslat odkaz"}
          </Button>
        </form>
      </CardContent>
      <CardFooter className="justify-center">
        <Link
          to="/login"
          className="text-sm text-muted-foreground hover:underline"
          data-testid="auth-forgot-back-login"
        >
          Zpět na přihlášení
        </Link>
      </CardFooter>
    </Card>
  );
}
